import * as PodcastServices from 'widget/PodcastProfile/services';
import baseLogger from 'widget/logger';
import qs from 'qs';
import { applyConfigToProps } from 'widget/PodcastProfile/helpers';
import {
  ConfigurableProps,
  InitialProps,
  InstanceMethods,
  PodcastProfileResolvedProps,
} from 'widget/PodcastProfile/types';
import { Episode } from 'state/Podcast/types';
import { getIdFromSlug } from 'state/Routing/helpers';
import { WIDGET_DIMENSIONS } from 'constants/widgetDimensions';
import { WidgetLocationConfig } from 'widget/types';

export default async function getInitialProps(
  { params, query }: InitialProps,
  { logger = baseLogger, transport }: InstanceMethods,
  { websiteUrl = '' }: WidgetLocationConfig,
): Promise<PodcastProfileResolvedProps> {
  const { id, namespace } = params;
  const { autoplay, episodeCount } = query;
  const podcastId = getIdFromSlug(id);
  const limit =
    episodeCount ? Math.min(10, Math.max(1, Number(episodeCount))) : 5;

  const { description, imageUrl, showType, title } =
    await PodcastServices.fetchPodcast({
      logger,
      podcastId,
      transport,
    });

  const sortAsc = showType === 'serial';

  const podcastEpisodes = await PodcastServices.fetchPodcastEpisodes({
    logger,
    podcastId,
    sortAsc,
    transport,
  });

  const episodeId = podcastEpisodes.at(0)?.id;
  const episodeTitle = podcastEpisodes.at(0)?.title;
  const episodes = podcastEpisodes.slice(0, limit);

  // query string parameters for analytics so that we can tell the user came to the episode profile / podcast profile from widget
  const trackParamsforLandingUrls = {
    keyid: [title],
    pname: 'podcast_profile',
    sc: 'widget_share',
  };

  const tracks: Array<{ stationId: string; trackId: string }> = [];

  if (podcastEpisodes.length > 0) {
    episodes.forEach((episode: Episode) =>
      tracks.push({
        stationId: String(podcastId),
        trackId: String(episode.id),
      }),
    );
  }

  const basePodcastUrl = `${websiteUrl}/${namespace}/${id}/`;

  const configureableProps: ConfigurableProps = applyConfigToProps(
    {
      description,
      imageUrl,
    },
    query,
  );

  return {
    autoplay: !!autoplay,
    basePodcastUrl,
    dimensions: { ...WIDGET_DIMENSIONS.PODCAST_PROFILE },
    episodeId: String(episodeId),
    episodes,
    followUrl: `${basePodcastUrl}?${qs.stringify({
      ...trackParamsforLandingUrls,
      follow: true,
    })}`,
    origQuery: query,
    podcastId: String(podcastId),
    podcastUrl: `${basePodcastUrl}?${qs.stringify(trackParamsforLandingUrls)}`,
    subtitle: episodeTitle,
    title,
    tracks,
    urlWithQuery: `${basePodcastUrl}?${qs.stringify(query)}`,
    ...configureableProps,
  };
}
